import React, { FC, ReactNode, useCallback } from 'react';
import { useLocation } from 'react-router';

import { EvFlexColumn, EvFlexRow, EvIcon, InfoMsgMarkIcon } from '@evinced-private/ui-common';

import { useBannerStore } from 'src/apps/dashboard/stores/bannerStore';
import { ClarificationBanner } from 'src/common/components/clarification-banner/ClarificationBanner';
import { RoutesHelper } from 'src/common/helpers/RoutesHelper';

import { LABELS } from '../../constants/Labels';
import { DashboardTabsNav } from '../dashboard-tabs-nav/DashboardTabsNav';

interface DashboardLayoutProps {
	children: ReactNode;
	showBanner?: boolean;
}

/**
 * Shared chrome rendered inside both Web and Mobile dashboard roots.
 * Provides the page padding, the (web-only) clarification banner, and
 * the top-level tabs nav (hidden on the issues page). The routed page
 * content is rendered as `children`.
 */
export const DashboardLayout: FC<DashboardLayoutProps> = ({ children, showBanner = true }) => {
	const location = useLocation();
	const { visible: isBannerVisible, hidePermanently, setVisible } = useBannerStore();

	const isIssuesPage =
		location.pathname === RoutesHelper.getDashboardIssuesPath() ||
		location.pathname === RoutesHelper.getMobileDashboardIssuesPath();

	const onHideBanner = useCallback(() => {
		setVisible(false);
	}, [setVisible]);

	return (
		<EvFlexColumn gap="LARGE" padding={['XLARGE', 'NONE']}>
			{isBannerVisible && showBanner && (
				<ClarificationBanner hide={onHideBanner} hidePermanently={hidePermanently}>
					<EvIcon icon={InfoMsgMarkIcon} small />
					<EvFlexRow>{LABELS.DASHBOARD.ACCESSIBILITY_DASHBOARD_DESCRIPTION}</EvFlexRow>
				</ClarificationBanner>
			)}

			{!isIssuesPage && (
				<EvFlexColumn>
					<DashboardTabsNav />
				</EvFlexColumn>
			)}

			<EvFlexRow>{children}</EvFlexRow>
		</EvFlexColumn>
	);
};
